import { formatPercent } from '@knitknot/shared/components/report/metrics';
import type { VisibilityRankingRow } from '@/hooks/api/useCompetitors';
import type { OwnedMoverRow } from '@/hooks/api/useSources';
import type { PlaybookListItem } from '@/types/playbooks';
import type { TrafficVisitsLane } from '@/hooks/api/useTrafficSummary';
import { visitsWeekClause } from '@/lib/aiVisits';

/* ────────────────────────────────────────────────────────────────────────────
   weekSummary — the clauses behind /home's one "since last week" line.

   It exists because the page could say WHAT moved (five rate series on the
   chart) and never WHO moved it. Every clause here names an ENTITY — a rival,
   one of your pages, a play you shipped — which is precisely what the chart
   cannot draw. That is also the rule that keeps this line from becoming a
   second legend: if a clause would restate a plotted metric, it doesn't belong
   here (rule 13 — the deleted /home tile strip was exactly that mistake).

   Three sources, three facts, NO causal claim between them. "Since last week"
   lists what happened; it never says one thing caused another, because nothing
   here establishes that.

   Every clause is independently gated and the line renders NOTHING when none
   qualify. A weekly summary that always has something to say is a template,
   and a reader learns to skip it within two weeks.
   ──────────────────────────────────────────────────────────────────────────── */

/** A rival has to move at least this many points before it is worth a sentence.
 *  Under it, the "gain" is inside the noise of which prompts ran that week. */
const MIN_RIVAL_PP = 1;

/** A page has to move at least this many citing answers. Small ±1/±2 swings
 *  are answer-set churn, not something anyone did. */
const MIN_PAGE_DELTA = 5;

/** Longest playbook title this line will quote before falling back to a count
 *  (see `shippedThisWeek`) — the whole summary has to stay ONE line. */
const MAX_NAMED_TITLE = 48;

export interface WeekSummaryInput {
  ranking: VisibilityRankingRow[];
  movers: OwnedMoverRow[];
  playbooks: PlaybookListItem[];
  /** Start of the current measurement period (ISO). Plays that shipped on or
   *  after it are "this week"; null disables the shipped clause rather than
   *  guessing a window. */
  periodStart: string | null;
  /** The 7-day AI-referred visits lane (`/traffic/summary`). Omitted/null
   *  disables the visits clause; the gate lives in `visitsWeekClause`. */
  visits?: TrafficVisitsLane | null;
}

/** The biggest rival RISE in share of discovery answers, in whole points —
 *  rounded the way the Visibility rail prints both sides (`percentPointDelta`'s
 *  rule), so the sentence and the rail can never disagree by a point. */
function topRivalGain(ranking: VisibilityRankingRow[]): string | null {
  let best: { name: string; pp: number } | null = null;
  for (const r of ranking) {
    if (r.entity === 'you' || !r.name) continue;
    const now = formatPercent(r.rate);
    const before = formatPercent(r.prev_rate);
    if (now == null || before == null) continue;
    const pp = now - before;
    if (pp < MIN_RIVAL_PP) continue;
    if (!best || pp > best.pp) best = { name: r.name, pp };
  }
  return best ? `${best.name} gained ${best.pp}pp of discovery answers` : null;
}

/** The biggest movement on one of your OWN pages, in citing answers. Losses
 *  and gains are both eligible and the larger absolute move wins — a page that
 *  fell out of the answers is the more useful fact, and suppressing it would
 *  make this line a highlight reel. */
function topPageMove(movers: OwnedMoverRow[]): string | null {
  let best: OwnedMoverRow | null = null;
  for (const m of movers) {
    if (Math.abs(m.delta) < MIN_PAGE_DELTA) continue;
    if (!best || Math.abs(m.delta) > Math.abs(best.delta)) best = m;
  }
  if (!best) return null;
  const verb = best.delta > 0 ? 'gained' : 'lost';
  const n = Math.abs(best.delta);
  return `${pageLabel(best)} ${verb} ${n} citing ${n === 1 ? 'answer' : 'answers'}`;
}

/** A short editorial label for a page in the Overview headline. Prefer the
 *  page title; fall back to a humanized final path segment. A raw URL belongs
 *  in a ledger, not in the page's largest sentence. */
function pageLabel(m: OwnedMoverRow): string {
  const supplied = m.title?.trim();
  if (supplied) return `Your “${truncateLabel(supplied)}” page`;
  try {
    const path = new URL(m.url).pathname;
    if (path === '/') return 'Your home page';
    const segment = decodeURIComponent(path.split('/').filter(Boolean).at(-1) ?? '')
      .replace(/[-_]+/g, ' ')
      .replace(/\bai\b/gi, 'AI')
      .replace(/\bqa\b/gi, 'QA')
      .replace(/\bgeo\b/gi, 'GEO')
      .trim();
    if (!segment) return 'One of your pages';
    const label = segment[0].toUpperCase() + segment.slice(1);
    return `Your “${truncateLabel(label)}” page`;
  } catch {
    return 'One of your pages';
  }
}

function truncateLabel(label: string): string {
  const max = 72;
  return label.length <= max ? label : `${label.slice(0, max - 1).trimEnd()}…`;
}

function shippedThisWeek(playbooks: PlaybookListItem[], periodStart: string | null): string | null {
  if (!periodStart) return null;
  const start = Date.parse(periodStart);
  if (Number.isNaN(start)) return null;
  const shipped = playbooks.filter((p) => {
    if (!p.shipped_at) return false;
    const at = Date.parse(p.shipped_at);
    return !Number.isNaN(at) && at >= start;
  });
  if (shipped.length === 0) return null;
  // One SHORT play is named; several — or one whose title would wrap the line —
  // are counted. Playbook titles are page headlines and run to 80+ characters
  // ("[journey-test] QA Managers' Guide to Evaluation Disputes, Calibration,
  // and Review Workflows"), which pushed this one-line summary onto two lines
  // in the very first render. A mid-sentence ellipsis would be worse than the
  // count, and the title is one click away either way.
  const only = shipped.length === 1 ? shipped[0].title : null;
  return only && only.length <= MAX_NAMED_TITLE
    ? `you shipped “${only}”`
    : `you shipped ${shipped.length} ${shipped.length === 1 ? 'playbook' : 'playbooks'}`;
}

/**
 * The clauses, in reading order: what a rival did, what your content did, what
 * you did, what people did (AI-referred visits — a fourth source, gated on
 * both the size of the move and the volume behind it). Empty array ⇒ the
 * caller renders nothing at all.
 */
export function weekSummary({
  ranking,
  movers,
  playbooks,
  periodStart,
  visits = null,
}: WeekSummaryInput): string[] {
  return [
    topRivalGain(ranking),
    topPageMove(movers),
    shippedThisWeek(playbooks, periodStart),
    visitsWeekClause(visits),
  ].filter((c): c is string => c != null);
}
